import {
  Component, Input, Output, EventEmitter,
  inject, OnChanges, OnInit, SimpleChanges, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  LocationService, Country, State, City, Timezone,
  PlaceSuggestion, LocationState
} from '../services/location.service';

@Component({
  selector: 'app-location-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.scss']
})
export class LocationPickerComponent implements OnChanges, OnInit, OnDestroy {
  private svc = inject(LocationService);
  private cdr = inject(ChangeDetectorRef);

  @Input()  open   = false;
  @Output() closed = new EventEmitter<void>();

  // ── Country ───────────────────────────────────────────────────────────────
  countrySearch       = '';
  countrySuggestions: PlaceSuggestion[] = [];
  loadingCountry      = false;
  resolvingCountry    = false;
  selectedCountry: Country | null = null;

  // ── State ─────────────────────────────────────────────────────────────────
  stateSearch        = '';
  stateSuggestions: PlaceSuggestion[] = [];
  loadingState       = false;
  stateDropdownOpen  = false;
  selectedState: State | null = null;

  // ── City ──────────────────────────────────────────────────────────────────
  citySearch       = '';
  citySuggestions: PlaceSuggestion[] = [];
  loadingCity      = false;
  cityDropdownOpen = false;
  selectedCity: City | null = null;

  // ── Timezone (auto-resolved via Google Time Zone API) ─────────────────────
  loadingTimezone      = false;
  selectedTimezone     = '';
  selectedTzDetail: Timezone | null = null;

  // ── Misc ──────────────────────────────────────────────────────────────────
  applying = false;
  private countryInput$ = new Subject<string>();
  private stateInput$   = new Subject<string>();
  private cityInput$    = new Subject<string>();
  private subs: Subscription[] = [];

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Debounced country search
    const cSub = this.countryInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(q => {
        if (q.trim().length < 2) { this.loadingCountry = false; return of([]); }
        return this.svc.searchCountries(q);
      })
    ).subscribe({
      next: (results) => {
        this.countrySuggestions = results;
        this.loadingCountry     = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loadingCountry = false; this.cdr.detectChanges(); },
    });

    // Debounced state search
    const sSub = this.stateInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(q => {
        if (q.trim().length < 2) { this.loadingState = false; return of([]); }
        return this.svc.searchStates(q, this.selectedCountry?.iso2 ?? '');
      })
    ).subscribe({
      next: (results) => {
        this.stateSuggestions = results;
        this.loadingState     = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loadingState = false; this.cdr.detectChanges(); },
    });

    // Debounced city search
    const ciSub = this.cityInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap(q => {
        if (q.trim().length < 2) { this.loadingCity = false; return of([]); }
        return this.svc.searchCities(q, this.selectedCountry?.iso2 ?? '');
      })
    ).subscribe({
      next: (results) => {
        this.citySuggestions = results;
        this.loadingCity     = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loadingCity = false; this.cdr.detectChanges(); },
    });

    this.subs.push(cSub, sSub, ciSub);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true) {
      this.resetDropdowns();
      this.initFromSnapshot();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private initFromSnapshot(): void {
    const snap = this.svc.snapshot;
    this.selectedCountry  = snap.country;
    this.selectedState    = snap.state;
    this.selectedCity     = snap.city;
    this.selectedTimezone = snap.timezone;
    this.selectedTzDetail = snap.country?.timezones?.[0] ?? null;
  }

  private resetDropdowns(): void {
    this.stateDropdownOpen  = false;
    this.cityDropdownOpen   = false;
    this.countrySearch      = '';
    this.stateSearch        = '';
    this.citySearch         = '';
    this.countrySuggestions = [];
    this.stateSuggestions   = [];
    this.citySuggestions    = [];
  }

  // ── Country ───────────────────────────────────────────────────────────────

  onCountryInput(): void {
    this.loadingCountry     = this.countrySearch.trim().length >= 2;
    this.countrySuggestions = [];
    this.countryInput$.next(this.countrySearch);
  }

  selectCountrySuggestion(s: PlaceSuggestion): void {
    this.countrySuggestions = [];
    this.countrySearch      = '';
    this.resolvingCountry   = true;
    this.resetStateCity();

    const sub = this.svc.resolveCountry(s.placeId).subscribe({
      next: (detail) => {
        this.selectedCountry = {
          _id:       s.placeId,
          cscId:     0,
          name:      detail.name || s.name,
          iso2:      detail.iso2,
          iso3:      '',
          emoji:     this.svc.flagEmoji(detail.iso2),
          timezones: [],
          status:    'active',
        };
        this.resolvingCountry = false;
        this.cdr.detectChanges();
      },
      error: () => {
        // Fallback: create country from suggestion name if details fail
        this.selectedCountry = {
          _id: s.placeId, cscId: 0, name: s.name,
          iso2: '', iso3: '', emoji: '🌐', timezones: [], status: 'active',
        };
        this.resolvingCountry = false;
        this.cdr.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  clearCountry(): void {
    this.selectedCountry = null;
    this.countrySearch   = '';
    this.selectedTimezone  = '';
    this.selectedTzDetail  = null;
    this.resetStateCity();
    this.resetDropdowns();
  }

  // ── State ─────────────────────────────────────────────────────────────────

  openStateDropdown(): void {
    this.stateDropdownOpen = true;
    this.cityDropdownOpen  = false;
    this.stateSearch       = '';
    this.stateSuggestions  = [];
  }

  closeStateDropdown(): void {
    this.stateDropdownOpen = false;
    this.stateSearch       = '';
    this.stateSuggestions  = [];
  }

  onStateInput(): void {
    this.loadingState     = this.stateSearch.trim().length >= 2;
    this.stateSuggestions = [];
    this.stateInput$.next(this.stateSearch);
  }

  selectStateSuggestion(s: PlaceSuggestion): void {
    this.selectedState     = { _id: s.placeId, name: s.name };
    this.selectedCity      = null;
    this.selectedTimezone  = '';
    this.selectedTzDetail  = null;
    this.citySuggestions   = [];
    this.citySearch        = '';
    this.stateDropdownOpen = false;
    this.stateSearch       = '';
    this.cdr.detectChanges();
  }

  clearState(): void {
    this.selectedState    = null;
    this.selectedCity     = null;
    this.selectedTimezone = '';
    this.selectedTzDetail = null;
    this.stateDropdownOpen = false;
    this.cityDropdownOpen  = false;
    this.stateSearch      = '';
    this.citySearch       = '';
    this.stateSuggestions = [];
    this.citySuggestions  = [];
  }

  // ── City ──────────────────────────────────────────────────────────────────

  openCityDropdown(): void {
    this.cityDropdownOpen  = true;
    this.stateDropdownOpen = false;
    this.citySearch        = '';
    this.citySuggestions   = [];
  }

  closeCityDropdown(): void {
    this.cityDropdownOpen = false;
    this.citySearch       = '';
    this.citySuggestions  = [];
  }

  onCityInput(): void {
    this.loadingCity     = this.citySearch.trim().length >= 2;
    this.citySuggestions = [];
    this.cityInput$.next(this.citySearch);
  }

  selectCitySuggestion(s: PlaceSuggestion): void {
    this.cityDropdownOpen = false;
    this.citySearch       = '';
    this.citySuggestions  = [];
    this.loadingTimezone  = true;
    this.selectedTimezone = '';
    this.selectedTzDetail = null;

    const sub = this.svc.resolvePlace(s.placeId).subscribe({
      next: (loc) => {
        this.selectedCity = { _id: s.placeId, name: loc.name || s.name, lat: loc.lat, lng: loc.lng };
        this.fetchTimezone(loc.lat, loc.lng);
        this.cdr.detectChanges();
      },
      error: () => {
        this.selectedCity    = { _id: s.placeId, name: s.name, lat: 0, lng: 0 };
        this.loadingTimezone = false;
        this.cdr.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  clearCity(): void {
    this.selectedCity     = null;
    this.selectedTimezone = '';
    this.selectedTzDetail = null;
    this.cityDropdownOpen = false;
    this.citySearch       = '';
    this.citySuggestions  = [];
  }

  // ── Timezone ──────────────────────────────────────────────────────────────

  private fetchTimezone(lat: number, lng: number): void {
    this.loadingTimezone = true;
    const sub = this.svc.fetchTimezone(lat, lng).subscribe({
      next: (tz) => {
        this.selectedTzDetail = tz;
        this.selectedTimezone = tz.zoneName;
        if (this.selectedCountry) {
          this.selectedCountry = { ...this.selectedCountry, timezones: [tz] };
        }
        this.loadingTimezone = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loadingTimezone = false; this.cdr.detectChanges(); },
    });
    this.subs.push(sub);
  }

  // ── Apply ─────────────────────────────────────────────────────────────────

  onApply(): void {
    if (this.applying || !this.selectedCountry) return;
    this.applying = true;
    const newState: LocationState = {
      country:        this.selectedCountry,
      state:          this.selectedState,
      city:           this.selectedCity,
      timezone:       this.selectedTimezone,
      timezoneAbbr:   this.selectedTzDetail?.tzName  ?? '',
      timezoneOffset: this.selectedTzDetail?.gmtOffsetName ?? '',
    };
    this.svc.setLocation(newState);
    this.applying = false;
    this.cdr.detectChanges();
    this.closed.emit();
  }

  onClose(): void {
    if (!this.applying) this.closed.emit();
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private resetStateCity(): void {
    this.selectedState    = null;
    this.selectedCity     = null;
    this.selectedTimezone = '';
    this.selectedTzDetail = null;
    this.stateSuggestions = [];
    this.citySuggestions  = [];
    this.stateSearch      = '';
    this.citySearch       = '';
  }
}
